<template>
  <div>
    <template v-for="match in matches">
      <TheClosedAndLiveSummerMatch
        :key="`upcoming-match-${match.id}`"
        :match="match"
        :my-score-predictions="getMyPredictedScoreForMatch(match.id)"
        :is-no-athlete-picked="!! isNoAthletePick"
      />
    </template>
    <template v-if="round.status === roundStatuses.closed">
      <InfoBlock
        v-if="!round.is_evaluated"
        :title="$t('waiting-to-be-judged')"
        :has-arrow="false"
        :icon="['far', 'clock']"
        title-color="blue"
      />
      <BaseLoading v-else-if="isPrizesLoading || isScoreCountsLoading" />
      <InfoBlock
        v-else
        :title="infoBlockTitle"
        :description="infoBlockDescription"
        :image="infoBlockImage"
        :variant="infoBlockVariant"
        :has-arrow="false"
      >
        <div
          v-if="prize"
          class="pt-3"
        >
          <router-link :to="{ name: 'prizes' }">
            <BaseButton
              variant="outline-orange"
              is-rounded
            >
              <span class="px-3">
                {{ statusText }}
              </span>
            </BaseButton>
          </router-link>
        </div>
      </InfoBlock>
    </template>
    <div
      id="subMenuNavigationWrapper"
      :class="[
        'd-flex justify-content-center py-4',
        $style.subMenu
      ]"
    >
      <router-link
        :to="{ name: 'round-detail' }"
        class="mr-1"
        @click.native="handleScrollToSubNav"
      >
        <BaseButton
          :variant="$route.name === 'round-detail' ? 'orange' : 'outline-orange'"
          font-size="small"
          left-rounded
        >
          <span class="px-1">{{ $t('goals') }}</span>
        </BaseButton>
      </router-link>

      <router-link
        v-if="round.status === roundStatuses.closed"
        :to="{ name: 'round-current-standings' }"
        @click.native="handleScrollToSubNav"
      >
        <BaseButton
          :variant="$route.name === 'round-current-standings' ? 'orange' : 'outline-orange'"
          font-size="small"
        >
          {{ $t('general-standings') }}
        </BaseButton>
      </router-link>

      <router-link
        :to="{ name: 'round-pool-standings' }"
        class="ml-1"
        @click.native="handleScrollToSubNav"
      >
        <BaseButton
          :variant="$route.name === 'round-pool-standings' ? 'orange' : 'outline-orange'"
          font-size="small"
          right-rounded
        >
          {{ $t('pool-standings') }}
        </BaseButton>
      </router-link>
    </div>
    <router-view />
  </div>
</template>

<script>
import VueScrollTo from 'vue-scrollto'
import { mapGetters, mapState } from 'vuex'
import InfoBlock from '@/pages/RoundDetail/Components/Atoms/InfoBlock'
import TheClosedAndLiveSummerMatch from '@/pages/RoundDetail/Components/Molecules/TheClosedAndLiveSummerMatch'
import BaseButton from '@/components/Atoms/BaseButton'
import { WIN_STATUSES } from '@/store/modules/prizes/wins'
import BaseLoading from '@/components/Atoms/BaseLoading'

export default {
  name: 'LiveAndClosedSummerRound',
  components: {
    BaseLoading,
    BaseButton,
    TheClosedAndLiveSummerMatch,
    InfoBlock,
  },
  props: {
    round: {
      type: Object,
      default: () => {},
    },
    matches: {
      type: Array,
      default: () => [],
    },
    roundStatuses: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapState({
      isScoreCountsLoading: state => state['rounds/scoreCounts'].isScoreCountsLoading,
      isPrizesLoading: state => state['prizes/index'].isPrizesLoading,
    }),
    ...mapGetters({
      getMyPredictedScoreForMatch: 'rounds/summer/matchScore/getPredictedScoreForMatch',
    }),
    scoreCounts() {
      return this.$store.getters['rounds/scoreCounts/getScoreCountsByRoundId']({
        roundId: this.round.id,
      })
    },
    firstPosition() {
      return this.scoreCounts.find(({ position }) => position === 1)
    },
    myPosition() {
      return this.scoreCounts.find(({ isMyPosition }) => isMyPosition)
    },
    prize() {
      return this.$store.getters['prizes/index/getPrizeByRound'](this.round.id)
    },
    infoBlockTitle() {
      if (!this.myPosition) {
        return this.$t('bad-luck')
      }
      if (this.myPosition.position === 2 && this.firstPosition.numOfPlayers >= 1) {
        return this.$t('almost-got-it')
      }
      return this.$t('congratulations')
    },
    infoBlockDescription() {
      if (!this.myPosition) {
        return this.$t('you-ve-missed-the-score-this-time')
      }
      if (this.myPosition.position === 2 && this.firstPosition.numOfPlayers >= 1) {
        return this.$t('you-ve-picked-the-right-score-but-someone-else-has-picked-right-athlete-as-well')
      }
      if (this.myPosition.position === 2) {
        return this.$t('you-have-chosen-the-correct-score')
      }
      return this.$t('you-ve-picked-the-right-score-and-the-right-scoring-player')
    },
    infoBlockVariant() {
      if (!this.myPosition || (this.myPosition.position === 2 && this.firstPosition.numOfPlayers >= 1)) {
        return this.$t('red')
      }
      return 'darkBlue'
    },
    infoBlockImage() {
      if (!this.myPosition || (this.myPosition.position === 2 && this.firstPosition.numOfPlayers >= 1)) {
        return 'tooBad.svg'
      }
      return 'trophy.png'
    },
    statusText() {
      switch (this.prize.status) {
      case WIN_STATUSES.READY_TO_CLAIM:
        return this.$t('win-statuses.claim-reward')
      case WIN_STATUSES.CLAIMED:
        return this.$t('win-statuses.claimed')
      case WIN_STATUSES.COMPLETE:
        return this.$t('win-statuses.completed')
      }
      return ''
    },
    isNoAthletePick() {
      return this.$store.getters['rounds/athletesInRound/getIsNoAthletePickForRound'](this.round.id)
    },
  },
  methods: {
    handleScrollToSubNav() {
      VueScrollTo.scrollTo('#subMenuNavigationWrapper', 500, {
        container: 'body',
        offset: document.getElementById('navbar').offsetHeight * -1,
      })
    },
  },
}
</script>

<style module lang="scss">
  @import "../../../../../assets/scss/variables";
  .subMenu {
    border-top: 2px solid $color-bleached-silk;
    border-bottom: 2px solid $color-bleached-silk;
  }
</style>
